import { FC, useState, useCallback, MouseEvent } from 'react';

import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';

import { AvailableToolsIcon } from '../../assets/Icons/Icons';
import { Tool } from '../../types';
import { useActiveToolsMenuCardStyles } from './styles/active-tools-menu-card.styles';
import { mcpChatTranslationRef } from '../../translation';

interface ActiveToolsMenuCardProps {
  availableTools: Tool[];
  toolsLoading: boolean;
  iconClassName?: string;
  triggerClassName?: string;
  align?: 'left' | 'right';
}

export const ActiveToolsMenuCard: FC<ActiveToolsMenuCardProps> = ({
  availableTools,
  toolsLoading,
  iconClassName,
  triggerClassName,
  align = 'left',
}) => {
  const classes = useActiveToolsMenuCardStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <IconButton
        size="small"
        className={triggerClassName}
        aria-label={t('activeToolsMenu.ariaLabels.trigger')}
        onClick={handleClick}
      >
        <AvailableToolsIcon className={iconClassName} />
      </IconButton>
      <Menu
        id="active-tools-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={classes.menuContainer}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: align,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: align,
        }}
        MenuListProps={{
          autoFocusItem: false,
          'aria-labelledby': 'active-tools-menu',
        }}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerText}>
            {t('activeToolsMenu.title')}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={t('activeToolsMenu.ariaLabels.close')}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </Box>
        <Divider />
        {toolsLoading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={16} />
            <Typography className={classes.loadingText}>
              {t('activeToolsMenu.loadingText')}
            </Typography>
          </Box>
        )}
        {!toolsLoading && availableTools.length > 0 && (
          <Box className={classes.toolsList}>
            {availableTools.map(tool => (
              <MenuItem key={tool.function.name} disableRipple>
                <ListItemText
                  primaryTypographyProps={{ className: classes.itemText }}
                >
                  {tool.function.name}
                </ListItemText>
              </MenuItem>
            ))}
          </Box>
        )}
        {!toolsLoading && availableTools.length === 0 && (
          <Typography className={classes.emptyText}>
            {t('activeToolsMenu.emptyText')}
          </Typography>
        )}
      </Menu>
    </>
  );
};
